import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  OnInit,
  DestroyRef,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, Location } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { SecurityContext, HostListener } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AnalyticsService } from '../../core/services/shared/analytics.service';

import { ImageGalleryComponent } from '../../shared/components/image-gallery/image-gallery.component';
import { FullscreenGalleryComponent } from '../../shared/components/fullscreen-gallery/fullscreen-gallery.component';
import { InquiryModalComponent } from '../../shared/components/inquiry-modal/inquiry-modal.component';
import { ProductRatingsComponent } from '../../shared/components/product-ratings/product-ratings.component';

import { PublicListingService } from '../../core/services/listing/public-listing.service';
import { UrlService } from '../../core/services/shared/url.service';
import { CartService } from '../../core/services/shared/cart.service';
import { SeoService } from '../../core/services/shared/seo.service';
import { normalizeImageUrl } from '../../core/utils/image.util';

interface MerchandiseDetailModel {
  id: number;
  productTypeId: number;
  title: string;
  description: string;
  safeDescription: string;
  safeDetails: string;
  price: number;
  status: string;
  images: { url: string; thumbUrl: string; type: string }[];
  features: { type: string; features: string[] }[];
  stockCount: number;
  reviews: any[];
  averageRating?: number;
  reviewCount: number;
  merchandiseId: number;
}

@Component({
  selector: 'app-merchandise-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ImageGalleryComponent,
    FullscreenGalleryComponent,
    InquiryModalComponent,
    ProductRatingsComponent,
  ],
  templateUrl: './merchandise-detail.component.html',
  styleUrls: ['../product-detail/product-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MerchandiseDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private productService = inject(PublicListingService);
  private cartService = inject(CartService);
  private destroyRef = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  private seoService = inject(SeoService);
  private urlService = inject(UrlService);
  private analytics = inject(AnalyticsService);
  private platformId = inject(PLATFORM_ID);

  merchandise = signal<MerchandiseDetailModel | null>(null);
  isLoading = signal<boolean>(true);

  // Selection state
  selectedSize = signal<string>('');
  selectedColor = signal<string>('');
  optionError = signal<string>('');

  isSharePopupOpen = signal<boolean>(false);
  isFullscreenOpen = signal<boolean>(false);
  fullscreenIndex = signal<number>(0);
  isInquiryOpen = signal<boolean>(false);
  inquiryType = signal<'VIEWING' | 'INTEREST'>('INTEREST');
  activeTab = signal<string>('details');
  mainTab = signal<'details' | 'ratings'>('details');

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const target = event.target as HTMLElement;
    const isTrigger = target.closest('.share-trigger-btn');
    const isDropdown = target.closest('.share-dropdown');

    if (!isTrigger && !isDropdown && this.isSharePopupOpen()) {
      this.isSharePopupOpen.set(false);
    }
  }

  ngOnInit(): void {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.seoService.applyRoute(`/merchandise/${this.route.snapshot.paramMap.get('id')}`);
      const res = data['product'];
      if (!res) {
        this.merchandise.set(null);
        this.isLoading.set(false);
        return;
      }
      const normalized = this.normalizeProduct(res);
      this.merchandise.set(normalized);
      this.isLoading.set(false);
      this.updateMetaTags(normalized);
      if (['available', 'sale pending'].includes(normalized.status.trim().toLowerCase())) {
        this.analytics.trackPageView(`/merchandise/${normalized.id}`);
      }
    });
  }

  isVideo(url: string | null | undefined): boolean {
    if (!url) return false;
    const cleanUrl = url.toLowerCase().split('?')[0];
    return cleanUrl.endsWith('.mp4');
  }

  private normalizeProduct(res: any): MerchandiseDetailModel {
    // getMerchProductDetails returns the full product DTO with a nested merchandise object
    const m = res.merchandise || res;
    const merchPhotos = m.merchandisePhotos || res.productMedia || [];
    const sortedPhotos = [...merchPhotos].sort((a, b) => {
      if (a.isThumbnail === true && b.isThumbnail !== true) return -1;
      if (b.isThumbnail === true && a.isThumbnail !== true) return 1;
      return 0;
    });

    let images: any[] = [];
    if (sortedPhotos.length > 0) {
      images = sortedPhotos.map((p: any) => ({
        url: normalizeImageUrl(p.photoUrl || p.photoThumbnailUrl || p.photoMediumUrl),
        thumbUrl: normalizeImageUrl(p.photoThumbnailUrl || p.photoMediumUrl || p.photoUrl),
        type: this.isVideo(p.photoUrl) ? 'video' : 'image',
      }));
    } else if (res.merchandiseThumbnailPhoto) {
      images.push({
        url: normalizeImageUrl(res.merchandiseThumbnailPhoto.photoUrl),
        thumbUrl: normalizeImageUrl(res.merchandiseThumbnailPhoto.photoUrl),
        type: 'image',
      });
    } else if (m.thumbnailPhoto) {
      images.push({
        url: normalizeImageUrl(m.thumbnailPhoto.photoUrl),
        thumbUrl: normalizeImageUrl(m.thumbnailPhoto.photoUrl),
        type: 'image',
      });
    }

    if (images.length === 0) {
      images.push({ url: 'assets/no-item.png', thumbUrl: 'assets/no-item.png', type: 'image' });
    }

    // Features grouping
    const featureGroups: { [key: string]: string[] } = {};
    (m.merchandiseFeatureMappings || []).forEach((fm: any) => {
      const type = fm.merchandiseFeature?.featureType || 'General';
      if (!featureGroups[type]) featureGroups[type] = [];
      featureGroups[type].push(fm.merchandiseFeature?.featureName);
    });

    const features = Object.keys(featureGroups).map((type) => ({
      type,
      features: featureGroups[type],
    }));

    const rawDescription = res.productDescription || m.merchandiseDescription || '';
    const rawDetails = m.otherDetails || '';

    return {
      id: res.productId || m.productId,
      merchandiseId: m.merchandiseId || res.merchandiseId,
      productTypeId: res.productTypeId || m.productTypeId,
      title: m.merchandiseName || res.productName || 'Merchandise Item',
      description: rawDescription,
      safeDescription: this.sanitizer.sanitize(SecurityContext.HTML, rawDescription) || '',
      safeDetails: this.sanitizer.sanitize(SecurityContext.HTML, rawDetails) || '',
      price: m.price || res.price || 0,
      status: m.status?.statusName || res.status?.statusName || 'Available',
      images,
      features,
      stockCount: m.merchandiseCount || m.stockCount || 0,
      reviews: res.productReviews || [],
      averageRating: res.averageRating || 0,
      reviewCount: (res.productReviews || []).length,
    };
  }

  shareTo(platform: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    const m = this.merchandise();
    if (!m) return;

    const url = this.urlService.getFrontendShareUrl(m.id, 'merchandises');
    const text = `Check out this ${m.title} on Seger Yachts!`;

    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      case 'instagram':
        this.shareLink();
        return;
    }

    if (shareUrl && isPlatformBrowser(this.platformId)) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    this.isSharePopupOpen.set(false);
  }

  shareLink(): void {
    const m = this.merchandise();
    if (!m || !isPlatformBrowser(this.platformId)) return;
    const url = this.urlService.getFrontendShareUrl(m.id, 'merchandises');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
          alert('Link copied to clipboard!');
          this.isSharePopupOpen.set(false);
      });
    }
  }

  openFullscreen(index: number): void {
    this.fullscreenIndex.set(index);
    this.isFullscreenOpen.set(true);
  }

  closeFullscreen(): void {
    this.isFullscreenOpen.set(false);
  }

  openInquiry(type: 'VIEWING' | 'INTEREST'): void {
    this.inquiryType.set(type);
    this.isInquiryOpen.set(true);
  }

  closeInquiry(): void {
    this.isInquiryOpen.set(false);
  }

  setTab(tab: string): void {
    this.activeTab.set(tab);
  }

  goBack(): void {
    this.location.back();
  }

  // Options Management
  selectSize(size: string): void {
    this.selectedSize.set(size);
    this.optionError.set('');
  }

  selectColor(color: string): void {
    this.selectedColor.set(color);
    this.optionError.set('');
  }

  addToCart(m: MerchandiseDetailModel): void {
    this.cartService.addToCart({
      productId: m.merchandiseId.toString(),
      name: m.title,
      price: m.price,
      quantity: 1,
      image: m.images[0]?.url || '',
      size: undefined,
      color: undefined,
    });
  }

  private updateMetaTags(m: MerchandiseDetailModel): void {
    const currentUrl = new URL(`/merchandise/${m.id}`, environment.publicUrl).toString();

    const title = m.title || 'Merchandise';
    
    // Comprehensive HTML stripping and cleanup
    const cleanDescription = (m.description || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const previewDescription = cleanDescription.length > 200 
      ? cleanDescription.substring(0, 197) + '...' 
      : cleanDescription || `View ${title} on League City Yacht Sales.`;

    this.seoService.setMeta({
      title: `${title} | League City Yacht Sales`,
      description: previewDescription,
      image: m.images.length > 0 && m.images[0].url !== 'assets/no-item.png' ? m.images[0].url : '',
      url: currentUrl
    });

    this.seoService.setRobots(['available', 'sale pending'].includes(m.status.trim().toLowerCase()));

    const schema = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": title,
      "image": m.images.length > 0 && m.images[0].url !== 'assets/no-item.png' ? m.images[0].url : '',
      "description": previewDescription,
      "offers": {
        "@type": "Offer",
        "priceCurrency": "USD",
        "price": m.price || 0,
        "availability": "https://schema.org/InStock",
        "url": currentUrl
      }
    };
    this.seoService.setSchema(schema);
  }
}
